import React from 'react';
import { CardActionsMenu } from '@/shared/components/ui';
import { IntegrationIcon, Icon } from '@/shared/icons';
import { COOLDOWN_COLORS, resolveConversationCooldown } from '../../utils/cooldown';
import { formatRelativeTime } from '../../utils/time';
import { AssigneeAvatar, ContactAvatar, ConversationCardShell, CooldownTrack, UnreadBadge } from './parts';
import type { ConversationCardViewProps } from './types';

/**
 * Atendimento 06 — "Barra de esfriamento" · papel COMPACTO (padrão do módulo).
 *
 * Mockup aprovado (versão corrigida): comparacao.html:1701–1716 · CSS :634–655.
 */
export const ConversationCardCompact: React.FC<ConversationCardViewProps> = ({
  facts,
  actions,
  onCardClick,
}) => {
  const cooldown = resolveConversationCooldown(facts.lastMessageAt, {
    waitingOnUs: facts.waitingOnUs,
    ...(facts.sla ? { sla: facts.sla } : {}),
  });
  const colors = COOLDOWN_COLORS[cooldown.level];
  const weSpokeLast = !facts.waitingOnUs;

  return (
    <ConversationCardShell conversationId={facts.id} density="compact" gap={6} onClick={onCardClick}>
      <div className="flex items-center gap-2 min-w-0">
        <ContactAvatar name={facts.contactName} picture={facts.contactPicture} size={24} />
        <span
          className="flex-1 min-w-0 truncate"
          style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.35, color: 'var(--theme-text)' }}
        >
          {facts.contactName}
        </span>
        <UnreadBadge
          count={facts.unreadCount}
          variant="inline"
          data-testid={`attendance-card-unread-${facts.id}`}
        />
        {actions.length > 0 && (
          <CardActionsMenu size="sm" actions={actions} data-testid={`attendance-card-menu-${facts.id}`} />
        )}
      </div>

      <div className="flex items-center min-w-0" style={{ gap: 6 }}>
        {facts.providerId ? (
          <span className="flex-none inline-flex items-center">
            <IntegrationIcon id={facts.providerId} size={14} variant="brand" />
          </span>
        ) : (
          <Icon name="message-circle" size={14} style={{ color: 'var(--theme-muted-text)' }} />
        )}
        <span
          className="min-w-0 truncate"
          data-testid={`attendance-card-preview-${facts.id}`}
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            color: weSpokeLast ? 'var(--theme-muted-text)' : 'var(--theme-card-text)',
          }}
        >
          {weSpokeLast && <span style={{ fontWeight: 600, color: 'var(--theme-text)' }}>Você: </span>}
          {facts.lastMessage || 'Sem mensagens'}
        </span>
      </div>

      <div
        className="flex items-center gap-2 min-w-0"
        data-testid={`attendance-card-cooldown-${facts.id}`}
        data-cooldown-level={cooldown.level}
        data-cooldown-source={cooldown.source}
      >
        <span
          className="flex-none"
          style={{ fontSize: 11, fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: colors.fg }}
        >
          {formatRelativeTime(facts.lastMessageAt)}
        </span>
        <CooldownTrack ratio={cooldown.ratio} color={colors.solid} />
        <AssigneeAvatar name={facts.assigneeName} picture={facts.assigneePicture} size={20} />
      </div>
    </ConversationCardShell>
  );
};
